import * as _ from 'lodash';
import { ChangeEvent, FormEvent, useMemo, useState } from 'react';
import { IOptnSrtrValidation } from '@nkr/models/index';
import { GridWrapper } from '@nkr/views/microsite/campaigns/styles';
import { FirstDataRenderedEvent, GridApi, GridReadyEvent, ICellRendererParams, IHeaderParams, ValueGetterParams } from 'ag-grid-community';
import { AgGridColumnProps, AgGridReact } from 'ag-grid-react';
import { FormControl } from 'react-bootstrap';
import { columnMap } from './column-map';
import SortableHeaderComponent from '../center/list/common/sortable-header';
import ReactHtmlParser from 'react-html-parser';

interface IProps {
    columns: string[],
    data: IOptnSrtrValidation[]
}

const Verify = (props: IProps) => {
    const [center, setCenter] = useState('');
    const [status, setStatus] = useState(-1);
    const [gridApi, setGridApi] = useState<GridApi>(null);

    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
        setGridApi(e.api);
    }

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    }

    const getColumns = () => {
        if (props.columns.length > 0) {
            let columns: AgGridColumnProps[] = [];

            columns.push({
                colId: '0',
                headerName: 'R#',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
                headerClass: 'optn-srtr-header',
                cellClass: 'data-cell',
                suppressMenu: true,
                width: 45,
                valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1,
                cellRendererFramework: (params: ICellRendererParams) => <>{params.value}</>
            });

            props.columns.filter(c => c != null).forEach((column, index) => {
                let c = columnMap.find(x => x.name == column);

                let gridColumn = {
                    colId: (index + 1).toString(),
                    headerName: column,
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>{ReactHtmlParser(c.header)}</SortableHeaderComponent>,
                    headerClass: 'optn-srtr-header',
                    cellClass: 'data-cell',
                    suppressMenu: true,
                    width: c.width,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        let data = params.data as IOptnSrtrValidation;
                        return <>{data.columns[index]}</>;
                    },
                };

                columns.push(gridColumn);
            });

            columns.push({
                colId: (props.columns.length + 1).toString(),
                headerName: 'Errors',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Errors</SortableHeaderComponent>,
                headerClass: 'optn-srtr-header',
                cellClass: 'data-cell',
                suppressMenu: true,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IOptnSrtrValidation;

                    if (data.errors.length > 0) {
                        return <div className='text-left text-danger'>{data.errors.join(', ')}</div>;
                    } else {
                        return <></>;
                    }
                }
            });

            columns.push({
                colId: (props.columns.length + 2).toString(),
                headerName: 'Status',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Status</SortableHeaderComponent>,
                headerClass: 'optn-srtr-header',
                cellClass: 'data-cell',
                suppressMenu: true,
                width: 60,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IOptnSrtrValidation;

                    return <>{data.errors.length > 0 ? <span className='text-danger' style={{ fontWeight: 600 }}>Invalid</span> : <span className='text-success'>Valid</span>}</>;
                }
            });

            return columns;
        } else {
            return [];
        }
    };

    const handleCenterChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        setCenter(e.currentTarget.value);
    };

    const handleStatusChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        let status = parseInt(e.currentTarget.value);
        setStatus(status);
    };

    let columns = getColumns();

    let filteredData = props.data.filter(x => {
        let statusFilter = true;

        if (status == 0) {
            statusFilter = x.rowMissingData == false && x.errors.length == 0;
        }

        if (status == 1) {
            statusFilter = x.rowMissingData == true || x.errors.length > 0;
        }

        let centerFilter = true;

        if (center != '') {
            centerFilter = x.columns[0] == center;
        }

        return statusFilter && centerFilter;
    })

    let columnDefs = useMemo(() => columns, []);
    let rowData = useMemo(() => filteredData, [filteredData]);

    let gridHeight = (props.data.length * 30) < 400 ? (props.data.length * 30) + 31 : 391;

    let statusDescription = status == 0 ? 'valid' : 'invalid';

    let centers = _.uniq(props.data.map(x => x.columns[0]));

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ marginRight: 5, fontSize: 12 }}>Filter by Center:</div>
                    <div>
                        <FormControl
                            componentClass='select'
                            bsSize='sm'
                            value={center}
                            onChange={handleCenterChange}
                        >
                            <option value={''}>- All -</option>
                            {centers.map((center, index) =>
                                <option key={'ctr-' + index} value={center}>{center}</option>
                            )}
                        </FormControl>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ marginRight: 5, fontSize: 12 }}>Filter by Status:</div>
                    <div>
                        <FormControl
                            componentClass='select'
                            bsSize='sm'
                            value={status}
                            onChange={handleStatusChange}
                        >
                            <option value={-1}>- All -</option>
                            <option value={0}>Valid</option>
                            <option value={1}>Invalid</option>
                        </FormControl>
                    </div>
                </div>
            </div>

            <GridWrapper className='ag-theme-bootstrap' style={{ height: gridHeight + 'px' }}>
                <AgGridReact
                    columnDefs={columnDefs}
                    rowData={rowData}
                    headerHeight={40}
                    rowHeight={30}
                    suppressCellSelection={true}
                    suppressContextMenu={true}
                    noRowsOverlayComponentFramework={() => <div>No {statusDescription} rows</div>}
                    onGridReady={onGridReady}
                    onFirstDataRendered={onFirstDataRendered}
                />
            </GridWrapper>

            <div>{filteredData.length} rows...</div>
        </>
    );
}

export default Verify;